export const REALTIME_CONNECTION_STATUS = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  OPEN: 'open',
  ERROR: 'error',
};

// Backoff schedule for an unexpected WebRTC failure (ICE drop, OpenAI closing
// the call). Length caps the number of attempts — after the last entry the
// store gives up rather than retrying forever.
export const REALTIME_RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 8000];

// Without a STUN server the browser only ever gathers host candidates — on a
// network behind a NAT that can't reach api.openai.com's media path with one
// (symmetric NAT, a restrictive firewall), ICE has no reflexive candidate to
// offer and fails outright ("WebRTC: ICE failed, add a STUN server"). Public
// Google STUN servers, not TURN: the media itself still flows straight to
// OpenAI, this only helps the browser discover its own reachable address.
export const REALTIME_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export const AUDIO_MONITOR_DEFAULTS = {
  // RMS above this counts as "the user can hear something".
  signalThreshold: 0.02,
  // Debounce counters, so the audible indicator doesn't flicker per sample.
  activeSamplesRequired: 3,
  silenceSamplesBeforeMute: 8,
  // Shared by the RMS path and the tone detector. Do NOT retune it to make the
  // tone detector react faster: activeSamplesRequired/silenceSamplesBeforeMute
  // are expressed in samples, and the openAiVoiceAudible they produce feeds
  // getIsWebRtcAudioRunning, which the backend throttles on. Changing this
  // silently changes all of that — add a separate tone interval instead.
  sampleIntervalMs: 120,
  // 48000 / 2048 = 23.4375Hz per bin (21.53Hz if the hardware context runs at
  // 44.1kHz), 42.7ms per FFT window.
  fftSize: 2048,

  // --- Analyser configuration ---------------------------------------------
  // All three are non-default on purpose; the browser defaults break the tone
  // detector below.
  //
  // Default 0.8 blends 80% of the previous read into every getFrequencyData
  // call, so consecutive reads are ~80% the same numbers by construction. Any
  // peak-stability metric measured on top of that is measuring the smoother,
  // not the signal — speech looks perfectly stable. Must be 0.
  analyserSmoothingTimeConstant: 0,
  analyserMinDecibels: -100,
  // Default -30 clamps loud bins at the top of the range. The argmax loop uses
  // strict `>`, so ties resolve to the *lowest* saturated bin — loud speech
  // then produces a degenerate, unmoving low-frequency "peak" that reads as a
  // rock-steady tone. -10 keeps real speech off the ceiling.
  analyserMaxDecibels: -10,

  // --- Runaway tone detection (see audio-monitor.js checkToneAnomaly) ------
  // Reported as a sustained "beeeee"/"zeee" appearing mid-reply, unrelated to
  // server/test load — the working theory is a browser/OS audio decode glitch
  // under CPU pressure.
  //
  // Safe to sit this low because the peer connection is recvonly and the
  // analyser runs on the *remote* stream: the microphone is never opened, so
  // room noise, mains hum and the patient's own voice are structurally
  // incapable of reaching this detector. The floor only has to exclude DC and
  // rumble.
  toneMinFrequencyHz: 180,
  // Peak level minus the mean level of the rest of the spectrum, in dB.
  // Deliberately a *prominence*, not a share-of-energy ratio: the suspected
  // artifact is a repeating 20ms Opus frame, which is periodic at 50Hz and so
  // has a harmonic comb spectrum spread over many bins. A single-bin ratio
  // misses a comb entirely; prominence stays large because the strongest
  // harmonic still towers over the inter-harmonic floor.
  //
  // Pure tone: 30-60dB. Speech formant over the log-mean: 10-20dB. Sibilant
  // plateau: under 10dB.
  tonePeakProminenceDb: 25,
  // How far the peak bin may drift, measured against the FIRST sample of the
  // run rather than the previous one — a formant sweeping 2 bins per frame
  // walks 20 bins across a 10-sample run and would pass a moving reference.
  // 2 bins is +/-47Hz across the whole run; a real tone does not move at all.
  tonePeakBinTolerance: 2,
  // Consecutive samples the signature must hold. This is the primary
  // false-positive defence, not the spectral thresholds: conversational speech
  // does not hold one spectral configuration past ~300-400ms (a sustained
  // vowel tops out around there), and a glitch tone lasts seconds.
  // 10 x 120ms = 1.2s.
  toneConsecutiveSamplesRequired: 10,
  // max(rms)/min(rms) across the run. Speech carries 3-8Hz syllabic amplitude
  // modulation far exceeding this; a glitch tone is flat. 2.5 is about 8dB.
  toneRmsStabilityRatio: 2.5,
  // Minimum time between two detections, so a misdetection can't thrash
  // playback. Persists across the monitor's own start()/stop() calls.
  toneHealCooldownMs: 8000,
  // Silence gap between pause() and play() during a heal — long enough to let
  // the browser actually tear down and rebuild its audio render path, short
  // enough not to read as a dropped call.
  toneHealPauseMs: 300,
};

// Inbound RTP stats are cumulative counters, so a single read at detection
// time says nothing without a baseline. Sampling at 1Hz into a short ring lets
// an anomaly report a *delta* over the window leading up to its onset.
// getStats() on one peer connection at this rate is free.
export const REALTIME_STATS_RING = { intervalMs: 1000, size: 15 };

// How far back before an anomaly's onset to diff the stats ring. Detection
// lands ~1.2s after onset (see toneConsecutiveSamplesRequired), so the window
// has to be anchored to onset, not to detection.
export const TONE_ONSET_LOOKBACK_MS = 3000;

// Window for "how many OpenAI errors landed around this anomaly". Purely a
// correlation probe — the buzz and `conversation_already_has_active_response`
// are believed independent (the data channel is receive-only, so the frontend
// provably never sent a response.create), and this is the measurement that
// confirms or kills that.
export const OPENAI_ERROR_CORRELATION_WINDOW_MS = 10000;

// Last N OpenAI error timestamps kept for the windowed count above.
export const OPENAI_ERROR_RING_SIZE = 50;
